import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { sortByName } from '../../../utils/sort';
import type { Category, CategorySpecification, SpecificationKey } from '../types';

interface UseCategoriesReturn {
  categories: Category[];
  addCategory: (name: string, optionIds?: string[], specifications?: CategorySpecification[]) => Promise<Category | null>;
}

// Populates option_ids/specifications via two split queries (avoids a LATERAL JOIN, same
// reasoning as useMachines' machine_options split) — needed so MachineFormPage can filter the
// options list and render the specifications form for the category the user picks.
async function withLinks(rows: Category[]): Promise<Category[]> {
  const ids = rows.map((c) => c.id);
  if (!ids.length) return rows;
  const [{ data: optionRows }, { data: specRows }] = await Promise.all([
    supabase.from('category_options').select('category_id, option_id').in('category_id', ids),
    supabase.from('category_specifications').select('category_id, specification_key, required').in('category_id', ids),
  ]);
  const optionIdsByCategory: Record<string, string[]> = {};
  (optionRows ?? []).forEach((row: { category_id: string; option_id: string }) => {
    (optionIdsByCategory[row.category_id] ??= []).push(row.option_id);
  });
  const specsByCategory: Record<string, CategorySpecification[]> = {};
  (specRows ?? []).forEach((row: { category_id: string; specification_key: string; required: boolean }) => {
    (specsByCategory[row.category_id] ??= []).push({ key: row.specification_key as SpecificationKey, required: row.required });
  });
  return rows.map((c) => ({
    ...c,
    option_ids: optionIdsByCategory[c.id] ?? [],
    specifications: specsByCategory[c.id] ?? [],
  }));
}

export function useCategories(orgId: string | null): UseCategoriesReturn {
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    if (!orgId) {
      setCategories([]);
      return;
    }
    const load = async () => {
      try {
        const { data } = await supabase
          .from('categories')
          .select('id, name, organization_id, archived, created_at')
          .eq('organization_id', orgId)
          .eq('archived', false)
          .order('name');
        setCategories(await withLinks((data ?? []) as Category[]));
      } catch {
        setCategories([]);
      }
    };
    load();
  }, [orgId]);

  const addCategory = async (
    name: string,
    optionIds: string[] = [],
    specifications: CategorySpecification[] = []
  ): Promise<Category | null> => {
    if (!orgId) return null;
    try {
      const { data, error } = await supabase
        .from('categories')
        .insert({ name, organization_id: orgId, archived: false })
        .select()
        .single();
      if (error) throw error;
      const created = data as Category;
      if (optionIds.length > 0) {
        const { error: optErr } = await supabase.from('category_options').insert(
          optionIds.map((option_id) => ({ category_id: created.id, option_id, organization_id: orgId }))
        );
        if (optErr) throw optErr;
      }
      if (specifications.length > 0) {
        const { error: specErr } = await supabase.from('category_specifications').insert(
          specifications.map((s) => ({
            category_id: created.id,
            specification_key: s.key,
            required: s.required,
            organization_id: orgId,
          }))
        );
        if (specErr) throw specErr;
      }
      const categoryWithLinks: Category = { ...created, option_ids: optionIds, specifications };
      setCategories((prev) => sortByName([...prev, categoryWithLinks]));
      return categoryWithLinks;
    } catch {
      return null;
    }
  };

  return { categories, addCategory };
}
